/**
 * @module app.pp
 */

import { ProviderService } from 'app/cd/services/provider-service';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { apiCall } from 'app/shared/lib/api/api';
import { ComponentBase, SortDirection, Sorting, StandardLoadOptions } from 'app/shared/lib/essette-site';
import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IPopup } from 'app/shared/elements/popup-base';
import { SaveContextCustomElement } from 'app/pp/components/admin/save-context';
import { Toaster } from 'app/shared/lib/toaster';
import { validate } from 'app/shared/elements/ess-rule';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import * as CD from 'app/cd/models/essette-api';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { I18N } from 'aurelia-i18n';


@autoinject()
export class InvitationsGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    protected invitations: ReadonlyArray<CD.ProviderUserInvitationContext> | null = null;

    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 5;

    protected modalTitle: string = '';
    protected validateForm = validate;
    protected $isValid: boolean = false;


    ////****************** Element References ***************************************////
    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $saveContext!: SaveContextCustomElement;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly providerService: ProviderService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {

        super(...rest);
    }

    protected bind(bindingContext: Object, overrideContext: Object): void {
        this.reload();
    }


    ////****************** Events ***************************************************////
    private async reload(): Promise<void> {
        await this.loadInvitations();
    }


    ////****************** Functions ************************************************////
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting: sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected editInvitation(invitationContext: CD.ProviderUserInvitationContext | null): void {
        if(this.$modal && this.$saveContext) {
            this.modalTitle = !!invitationContext?.id ? 'ui:actions.edit' : 'ui:labels.new';
            this.$saveContext.loadInvitationContext(invitationContext);
            this.$modal.open();
        }
    }

    protected handleInvitationSaved(event: FormSuccessCustomEvent) {
        this.$modal.close();
        this.reload();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadInvitations(): Promise<void> {
        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: false
        };
        const includes: string[] = [
            'invitation',
            'homepage',
            'providerLocations',
            'providerLocations.facility',
            'roles',
            'invitation.displayTimeZone',
            'invitation.metadata.created.byUser'
        ];

        try {
            this.state.beginLoad();
            const response = await this.providerService.loadProviderUserInvitationContexts(options, includes);
            this.invitations = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async deactivateInvitation(invitation: CD.ProviderUserInvitation): Promise<void> {
        const result = await confirmation.showDeactivate('pp:providerUserInvitation.$', 1, false);
        if(result.dialogResponse === confirmation.options.CANCEL) { return; }

        if(!(result.dialogResponse === confirmation.options.OK)) {
            return;
        }

        try {
            this.state.beginSave();
            const response = await this.providerService.deactivateProviderUserInvitation(invitation);
            this.toaster.standard.deleted("pp:providerUserInvitation.$", response.isSuccessful);
            this.reload();
        } catch(error) {
            throw error;
        } finally {
            this.state.endSave();
        }
    }

    @apiCall
    protected async resendInvitation(invitation: CD.ProviderUserInvitation): Promise<void> {
        const result = await confirmation.showCustom(this.i18n.tr('pp:local.resendInvitationConfirm'), null, confirmation.options.StandardConfirm, false);
        if(result.dialogResponse === confirmation.options.CANCEL) { return; }

        if(!(result.dialogResponse === confirmation.options.OK)) {
            return;
        }

        try {
            this.state.beginSave();
            const response = await this.providerService.resendProviderUserInvitation(invitation);
            this.toaster.standard.updated("pp:providerUserInvitation.$", response.isSuccessful);
            this.reload();
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

}
